import React, { useState } from "react";
import { Save, Check, Trash2, X, Scroll, ArrowRight, Clock, Waves } from "lucide-react";
import Button from "../../components/ui/Button";
import {
  TERRAIN_TYPES,
  WEATHER_TYPES,
  MARINE_CONDITIONS,
  EVENT_TYPES,
  getTerrainMod,
  getWeatherMod,
  getMarineMod,
  isMarineTerrain,
} from "./api";
import { computeDistance, computeLegTime, eventDurationDays } from "./PathManager";
import { formatDurationDays, formatDatePlus, formatDistance } from "./formatDuration";

/**
 * Travel Summary modal shown before confirming a path.
 * Actions: Conferma (advances clock + saves + logs), Salva (only saves path + log),
 * Cancella (discard).
 */
export default function TravelSummary({
  draftPath,
  legs,
  travelModes,
  scaleInfo,
  imgSize,
  map,
  customMods,
  defaultFrom = "",
  defaultTo = "",
  onClose,
  onConfirmTime,
  onSavePath,
  onDiscard,
}) {
  const points = draftPath?.points || [];
  const distances = legs.map((_, i) => computeDistance(points[i], points[i + 1], imgSize, scaleInfo));
  const durations = legs.map((leg, i) => computeLegTime(distances[i], leg, travelModes, customMods));

  const totalDistance = distances.reduce((a, b) => a + b, 0);
  const totalTravelDays = durations.reduce((a, b) => a + b, 0);
  const totalEventDays = legs.reduce((a, l) => a + eventDurationDays(l?.event), 0);
  const totalDays = totalTravelDays + totalEventDays;

  const transports = [...new Set(legs.map((l) => travelModes.find((m) => m.id === l.transportId)?.name).filter(Boolean))];

  const [name, setName] = useState("Nuovo Viaggio");
  const [description, setDescription] = useState("");
  const [fromPlace, setFromPlace] = useState(defaultFrom);
  const [toPlace, setToPlace] = useState(defaultTo);

  const startDate = formatDatePlus(map || {}, 0, map?.months);
  const endDate = formatDatePlus(map || {}, totalDays, map?.months);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-background/85 backdrop-blur-sm"
      data-testid="travel-summary-modal"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="rune-border bg-card rounded-lg w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Scroll className="w-6 h-6 text-gold" />
            <div>
              <h3 className="font-display text-2xl text-gold">Riepilogo del Viaggio</h3>
              <p className="font-serif-fantasy italic text-sm text-muted-foreground">
                « Prima di partire, rileggi il cammino »
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            data-testid="summary-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto scrollbar-thin flex-1 space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Distanza totale" value={formatDistance(totalDistance, scaleInfo?.scale_unit)} />
            <Stat label="Tempo di viaggio" value={formatDurationDays(totalTravelDays)} />
            <Stat label="Eventi" value={formatDurationDays(totalEventDays)} />
            <Stat label="Durata totale" value={formatDurationDays(totalDays)} highlight />
          </div>

          {/* Departure / Arrival editable */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rune-border rounded-md p-3 bg-card/50">
              <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-1">Partenza</p>
              <input
                value={fromPlace}
                onChange={(e) => setFromPlace(e.target.value)}
                placeholder="Punto di partenza (nome)"
                className="w-full bg-input border border-gold/20 rounded px-2 py-1 text-sm mb-2"
                data-testid="summary-from-input"
              />
              <div className="flex items-center gap-2 text-xs text-parchment">
                <Clock className="w-3.5 h-3.5 text-gold" />
                <span data-testid="summary-start-date">{startDate}</span>
              </div>
            </div>
            <div className="rune-border rounded-md p-3 bg-card/50">
              <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-1">Arrivo</p>
              <input
                value={toPlace}
                onChange={(e) => setToPlace(e.target.value)}
                placeholder="Destinazione (nome)"
                className="w-full bg-input border border-gold/20 rounded px-2 py-1 text-sm mb-2"
                data-testid="summary-to-input"
              />
              <div className="flex items-center gap-2 text-xs text-parchment">
                <Clock className="w-3.5 h-3.5 text-gold" />
                <span data-testid="summary-end-date">{endDate}</span>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-heading text-[10px] tracking-widest text-gold uppercase mb-1">Nome del percorso</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
                data-testid="summary-name-input"
              />
            </div>
            <div>
              <label className="block font-heading text-[10px] tracking-widest text-gold uppercase mb-1">Descrizione narrativa</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="La compagnia partì all'alba..."
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
                data-testid="summary-desc-input"
              />
            </div>
          </div>

          {/* Transports */}
          <div>
            <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-2">Mezzi Utilizzati</p>
            <div className="flex flex-wrap gap-2">
              {transports.length === 0 ? (
                <span className="text-xs italic text-muted-foreground">Nessuno</span>
              ) : (
                transports.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gold/40 bg-gold/10 text-gold font-heading text-[10px] tracking-widest uppercase">
                    {t}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Legs detail (each with its embedded event) */}
          <div>
            <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-2">Tappe ({legs.length})</p>
            <div className="space-y-2">
              {legs.map((leg, i) => {
                const terr = TERRAIN_TYPES.find((t) => t.id === leg.terrain) || TERRAIN_TYPES[0];
                const marine = isMarineTerrain(leg.terrain);
                const cond = marine
                  ? (MARINE_CONDITIONS.find((m) => m.id === leg.marineCondition) || MARINE_CONDITIONS[0])
                  : (WEATHER_TYPES.find((w) => w.id === leg.weather) || WEATHER_TYPES[0]);
                const terrMod = getTerrainMod(leg.terrain, customMods);
                const condMod = marine ? getMarineMod(leg.marineCondition, customMods) : getWeatherMod(leg.weather, customMods);
                const mode = travelModes.find((m) => m.id === leg.transportId);
                const ev = leg.event;
                return (
                  <div key={i} className="p-3 border border-border rounded bg-card/40 text-xs">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-heading text-gold flex items-center gap-1">
                        Tappa {i + 1} {marine && <Waves className="w-3 h-3 text-arcane" />}
                      </p>
                      <p className="text-parchment">{formatDistance(distances[i], scaleInfo?.scale_unit)} · {formatDurationDays(durations[i])}</p>
                    </div>
                    <div className="text-muted-foreground">
                      {mode?.name} <span className="text-gold">({mode?.speed}/g)</span> · {terr.label}{" "}
                      <span className="text-gold">×{terrMod}</span> · {cond.label}{" "}
                      <span className="text-arcane">×{condMod}</span>
                    </div>
                    {ev && ev.type && (
                      <div className="mt-2 pt-2 border-t border-border/50 text-parchment">
                        <span className="font-heading text-[10px] text-gold tracking-widest uppercase">Evento:</span>{" "}
                        {EVENT_TYPES.find((t) => t.id === ev.type)?.label}
                        {ev.type === "encounter" && ev.description && (
                          <p className="italic text-muted-foreground mt-1">« {ev.description} »</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border flex flex-col sm:flex-row gap-2 justify-between">
          <Button variant="destructive" size="sm" onClick={onDiscard} data-testid="summary-discard-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Cancella
          </Button>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button
              variant="secondary" size="sm"
              onClick={() => onSavePath({ name, description, fromPlace, toPlace, startDate, endDate, legs, totalDistance, totalDays, transports })}
              data-testid="summary-save-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Salva Percorso
            </Button>
            <Button
              variant="primary" size="sm"
              onClick={() => onConfirmTime({ name, description, fromPlace, toPlace, startDate, endDate, legs, totalDistance, totalDays, transports })}
              data-testid="summary-confirm-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Conferma e Avanza col Tempo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rune-border rounded-md p-2 text-center ${highlight ? "bg-gold/10 gold-glow" : "bg-card/50"}`}>
      <p className="font-heading text-[9px] tracking-widest text-muted-foreground uppercase">{label}</p>
      <p className={`font-display text-lg ${highlight ? "text-gold" : "text-parchment"}`}>{value}</p>
    </div>
  );
}
